import { useQuery, QueryFunction } from '@tanstack/react-query'
import { useFetchWithAuth } from '../session/useFetchWithAuth'
import { ProfileMatch } from './types'

const URL = import.meta.env.VITE_EPC_API_URL ?? ''

// Keeping MatchGroup for backward compatibility
export interface MatchGroup {
  name: string
  description: string
  profiles: string[]
  members: ProfileMatch[]
}

export interface SuggestedMatchesResponse {
  success: boolean
  data: ProfileMatch[]
  pagination: {
    totalItems: number
    totalPages: number
    currentPage: number
    hasNextPage: boolean
    hasPreviousPage: boolean
    nextCursor?: string
    previousCursor?: string
  }
}

/**
 * Hook to fetch AI-grouped suggested profile matches for an event
 *
 * @param eventId - The ID of the event to get suggested matches for
 * @param page - The page number to fetch (defaults to 1)
 * @param pageSize - The number of items per page (defaults to 20)
 * @returns Query result with matches grouped by AI analysis
 */
export const useEventSuggestedMatches = (
  eventId: string,
  page: number = 1,
  pageSize: number = 20
) => {
  const fetchWithAuth = useFetchWithAuth()
  const getSuggestedMatches: QueryFunction<SuggestedMatchesResponse> = async () => {
    // Validate pagination parameters
    const validatedPage = Math.max(1, page)
    const validatedPageSize = Math.min(Math.max(1, pageSize), 100)

    const response = await fetchWithAuth(
      `${URL}/events/${eventId}/suggested-matches?page=${validatedPage}&pageSize=${validatedPageSize}`,
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      }
    )
    return response
  }

  return useQuery({
    queryKey: ['eventSuggestedMatches', eventId, page, pageSize] as const,
    queryFn: getSuggestedMatches,
    retry: false,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    enabled: Boolean(eventId) && eventId !== 'new',
    refetchOnWindowFocus: false, // Don't refetch when window regains focus since this is a computationally expensive operation
  })
}
